import {
  Button,
  EmptyState,
  Input,
  PageHeader,
  Skeleton,
  cn,
} from '@sitewise/ui';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Building2, Hammer, Home, Plus, Search, Upload } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { listBuildings, type BuildingSummary } from '../../../api/buildings.js';

export function BuildingsListPage() {
  const [q, setQ] = useState('');
  const buildingsQuery = useQuery({
    queryKey: ['buildings', q],
    queryFn: () => listBuildings({ q: q || undefined, limit: 50 }),
  });

  const buildings = buildingsQuery.data?.buildings ?? [];

  return (
    <>
      <PageHeader
        title="Buildings"
        description="Your portfolio of physical addresses, with units and on-site contacts."
        actions={
          <>
            <Link to="/buildings/import">
              <Button variant="outline" size="sm">
                <Upload className="h-4 w-4" />
                Import CSV
              </Button>
            </Link>
            <Link to="/buildings/new">
              <Button size="sm">
                <Plus className="h-4 w-4" />
                Add building
              </Button>
            </Link>
          </>
        }
      />

      <div className="mb-4 flex items-center gap-2">
        <div className="relative w-full max-w-sm">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-muted" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name or address"
            className="pl-8"
          />
        </div>
      </div>

      {buildingsQuery.isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-44 w-full rounded-xl" />
          ))}
        </div>
      ) : buildings.length === 0 ? (
        q ? (
          <p className="text-sm text-fg-muted">No buildings matched &ldquo;{q}&rdquo;.</p>
        ) : (
          <EmptyState
            icon={Building2}
            title="No buildings yet"
            description="Add one manually or import a CSV to get started."
            action={
              <Link to="/buildings/new">
                <Button size="sm">
                  <Plus className="h-4 w-4" />
                  Add building
                </Button>
              </Link>
            }
          />
        )
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {buildings.map((b) => (
            <BuildingCube key={b.id} building={b} />
          ))}
        </div>
      )}
    </>
  );
}

function BuildingCube({ building: b }: { building: BuildingSummary }) {
  return (
    <Link
      to={`/buildings/${b.id}`}
      className={cn(
        'group flex min-h-[11rem] flex-col justify-between rounded-xl border border-border bg-surface p-5',
        'transition-colors hover:border-fg-muted hover:bg-surface-muted',
      )}
    >
      <div className="min-w-0">
        <div className="flex items-start gap-2">
          <Building2 className="mt-0.5 h-4 w-4 shrink-0 text-fg-muted" />
          <div className="min-w-0">
            <h2 className="truncate text-lg font-semibold tracking-tight text-fg group-hover:underline">
              {b.name}
            </h2>
            <p className="mt-1 line-clamp-2 text-sm text-fg-muted">
              {b.addressLine1}
              {b.addressLine2 ? `, ${b.addressLine2}` : ''}
              <br />
              {b.city}, {b.state} {b.postalCode}
            </p>
          </div>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-3 gap-2 border-t border-border pt-3">
        <CubeStat icon={Home} label="Units" value={b.unitCount} />
        <CubeStat icon={Hammer} label="Open WOs" value={b.openWorkOrders ?? 0} />
        <CubeStat
          icon={AlertTriangle}
          label="Violations"
          value={b.openViolations ?? 0}
          emphasize={(b.openViolations ?? 0) > 0}
        />
      </div>
    </Link>
  );
}

function CubeStat({
  icon: Icon,
  label,
  value,
  emphasize,
}: {
  icon: typeof Home;
  label: string;
  value: number;
  emphasize?: boolean;
}) {
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide text-fg-muted">
        <Icon className="h-3 w-3" />
        <span className="truncate">{label}</span>
      </div>
      <div
        className={cn(
          'mt-0.5 text-xl font-semibold tabular-nums',
          emphasize ? 'text-danger' : 'text-fg',
        )}
      >
        {value.toLocaleString()}
      </div>
    </div>
  );
}
